/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Field, useField } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { PriceInputField } from "../../components";
import { useStrings } from "../../localization/localizationHelpers";
import { getIsSavingForm } from "../../redux/selectors";
import { CheckBoxOptionEnabler } from "../CheckBoxOptionEnabler";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FieldHeader } from "../FieldHeader";
import { MultiLineTextField } from "../MultiLineTextField";
import * as styles from "./PricingPanel.module.css";

export enum PricingOptions {
  Premium = "premium",
  FreeToPlay = "freeToPlay",
}

const renderOption = (
  name: string,
  label: string,
  pricingOption: PricingOptions,
  setSelection: (_value: React.SetStateAction<PricingOptions>) => void,
  disabled?: boolean
) => (
  <label className={styles.option}>
    <Field
      type="radio"
      name={name}
      value={pricingOption}
      data-testid={`${name}-${pricingOption}`}
      onClick={() => setSelection(pricingOption)}
      disabled={disabled}
    />
    {label}
  </label>
);

export const PricingPanel = () => {
  const [
    monetizationStrategy,
    premium,
    freeToPlay,
    suggestedRetailPrice,
    indicateHowGameWillGenerateRevenue,
    whatRetentionMechanicsAreInGame,
    doesYourF2pGameHaveAnInGameStore,
    describeYourInGamePricePoints,
    describeWhatPlayersCanSpendOn,
    howLongUntilANewPlayerWillPay,
    canPlayersTradeOrGiftItems,
    describeHowItemsCanBeGifted,
    isThereASubscriptionFee,
    describeYourSubscriptionModel,
  ] = useStrings(
    "monetizationStrategy",
    "premium",
    "freeToPlay",
    "suggestedRetailPrice",
    "indicateHowGameWillGenerateRevenue",
    "whatRetentionMechanicsAreInGame",
    "doesYourF2pGameHaveAnInGameStore",
    "describeYourInGamePricePoints",
    "describeWhatPlayersCanSpendOn",
    "howLongUntilANewPlayerWillPay",
    "canPlayersTradeOrGiftItems",
    "describeHowItemsCanBeGifted",
    "isThereASubscriptionFee",
    "describeYourSubscriptionModel"
  );
  const [field] = useField({ name: "monetizationStrategy" });
  const [selection, setSelection] = useState<PricingOptions>(field.value);
  const isSavingFormToCrm = useSelector(getIsSavingForm);

  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <FieldHeader
          titleClassName={styles.optionMargin}
          title={monetizationStrategy}
          isRequiredField={true}
        />
        <div role="group" aria-labelledby="radio-options-group">
          <div className={styles.optionsGroups}>
            {renderOption(
              field.name,
              premium,
              PricingOptions.Premium,
              setSelection,
              isSavingFormToCrm
            )}
            {renderOption(
              field.name,
              freeToPlay,
              PricingOptions.FreeToPlay,
              setSelection,
              isSavingFormToCrm
            )}
          </div>
        </div>
        {selection === PricingOptions.Premium && (
          <PriceInputField
            title={suggestedRetailPrice}
            name={"suggestedRetailPrice"}
            isRequiredField={true}
            isSaving={isSavingFormToCrm}
          />
        )}
        {selection === PricingOptions.FreeToPlay && (
          <>
            <MultiLineTextField
              title={indicateHowGameWillGenerateRevenue}
              name={"howItWillGenerateMoney"}
              isRequiredField={true}
              isSaving={isSavingFormToCrm}
            />
            <MultiLineTextField
              title={whatRetentionMechanicsAreInGame}
              name={"retentionMechanics"}
              isRequiredField={true}
              isSaving={isSavingFormToCrm}
            />
            <CheckBoxOptionEnabler
              name={"hasInGameStore"}
              text={doesYourF2pGameHaveAnInGameStore}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeYourInGamePricePoints}
                name={"inGamePricePoints"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
              />
              <MultiLineTextField
                title={describeWhatPlayersCanSpendOn}
                name={"whatPlayersCanSpendOn"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
              />
              <MultiLineTextField
                title={howLongUntilANewPlayerWillPay}
                name={"timeUntilAPlayerIsPromptedToPay"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
              />
            </CheckBoxOptionEnabler>
            <CheckBoxOptionEnabler
              name={"canPlayersTradeOrGiftItems"}
              text={canPlayersTradeOrGiftItems}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeHowItemsCanBeGifted}
                name={"howItemsCanBeGifted"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
              />
            </CheckBoxOptionEnabler>
            <CheckBoxOptionEnabler
              name={"hasSubscriptionFee"}
              text={isThereASubscriptionFee}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeYourSubscriptionModel}
                name={"subscriptionModel"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
              />
            </CheckBoxOptionEnabler>
          </>
        )}
        <FieldErrorMessage fieldName={field.name} />
      </Column>
    </Row>
  );
};
